/**
 * ds-bundle — seletor de pacote no bloco de compra.
 *
 * Responsabilidades, e so estas:
 *  1. abrir/fechar o painel das duas pecas conforme o radio;
 *  2. esconder o variant-picker do topo enquanto o pacote esta marcado, porque
 *     a cor passa a ser escolhida dentro do painel;
 *  3. manter o input[name="id"] apontando para a variante escolhida no card 1;
 *  4. recalcular os precos exibidos (card, total do pacote e economia).
 *
 * Quem monta o /cart/add.js continua sendo o assets/ds-addons.js: ele le o radio
 * marcado e o data-extra que este arquivo mantem atualizado. Um so lugar fala
 * com o carrinho.
 */
class DsBundle extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;

    this.#panel = this.querySelector('[data-ds-bundle-panel]');
    this.#kitRadio = this.querySelector('input[value="kit"]');

    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      radio.addEventListener('change', () => this.#sync(), { signal });
    }
    for (const select of this.querySelectorAll('[data-ds-bundle-variant]')) {
      select.addEventListener('change', () => this.#onVariant(select), { signal });
    }

    this.#semQuantidade();
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#mostrarPickerDoTopo(true);
  }

  #panel = null;
  #kitRadio = null;

  /**
   * Nao ha o que remover antes de adicionar: um "-" solitario ao lado do botao
   * so confunde. Escondemos o seletor de quantidade do bloco de compra (o da
   * barra fixa fica, porque la ele nao aparece).
   */
  #semQuantidade() {
    for (const el of document.querySelectorAll('.quantity-selector-wrapper, quantity-selector-component')) {
      if (el.closest('[class*="sticky"]')) continue;
      el.style.display = 'none';
    }
  }

  #kitMarcado() {
    return !!this.#kitRadio?.checked;
  }

  #idInput() {
    return (
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ??
      document.querySelector('input[name="id"]')
    );
  }

  /**
   * O variant-picker do Horizon nao tem um seletor estavel entre versoes, entao
   * procuramos por alguns nomes conhecidos e caimos fora sem quebrar se nenhum
   * existir. Esconder e uma decisao de UI: mostrar dois seletores da mesma cor
   * na mesma tela e convite a erro.
   */
  #pickerDoTopo() {
    return document.querySelector(
      'variant-picker, .variant-picker, [data-block-type="variant-picker"], .product-form__variants'
    );
  }

  #mostrarPickerDoTopo(mostrar) {
    const el = this.#pickerDoTopo();
    if (!el) return;
    el.hidden = !mostrar;
    el.style.display = mostrar ? '' : 'none';
  }

  #selectDe(hook) {
    return this.querySelector(`[data-ds-bundle-variant="${hook}"]`);
  }

  #opcao(hook) {
    const s = this.#selectDe(hook);
    return s?.selectedOptions?.[0] ?? null;
  }

  #cents(hook, campo) {
    const o = this.#opcao(hook);
    if (o) return Number(o.dataset[campo]) || 0;
    // sem select (produto de variante unica): usa o que veio renderizado
    const el = this.querySelector(`[data-ds-card-${campo === 'price' ? 'price' : 'was'}="${hook}"]`);
    return this.#parse(el?.textContent);
  }

  #parse(txt) {
    if (!txt) return 0;
    const n = Number(String(txt).replace(/[^0-9.,]/g, '').replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  #money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: 'USD',
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #onVariant(select) {
    const hook = select.dataset.dsBundleVariant;
    const id = select.value;

    if (hook === 'main') {
      // O card 1 E o produto da pagina: quem compra o pacote escolhe a cor aqui,
      // entao o input escondido do form precisa acompanhar.
      const input = this.#idInput();
      if (input && id) {
        input.value = id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      // O extra viaja no data-extra do radio, que e o que o ds-addons le.
      const o = this.#opcao('extra');
      if (this.#kitRadio && o) {
        this.#kitRadio.dataset.extra = o.value;
        this.#kitRadio.dataset.extraCents = o.dataset.price || '0';
        this.#kitRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.#render();
  }

  #sync() {
    const kit = this.#kitMarcado();
    if (this.#panel) this.#panel.hidden = !kit;
    this.#mostrarPickerDoTopo(!kit);
    this.#render();
  }

  #render() {
    for (const hook of ['main', 'extra']) {
      const o = this.#opcao(hook);
      if (!o) continue;
      const preco = Number(o.dataset.price) || 0;
      const antes = Number(o.dataset.compare) || 0;
      const el = this.querySelector(`[data-ds-card-price="${hook}"]`);
      if (el) el.textContent = this.#money(preco);
      const was = this.querySelector(`[data-ds-card-was="${hook}"]`);
      if (was) {
        if (antes > preco) {
          was.textContent = this.#money(antes);
          was.hidden = false;
        } else {
          was.hidden = true;
        }
      }
    }

    const soma = this.#cents('main', 'price') + this.#cents('extra', 'price');
    const somaAntes =
      Math.max(this.#cents('main', 'compare'), this.#cents('main', 'price')) +
      Math.max(this.#cents('extra', 'compare'), this.#cents('extra', 'price'));

    const total = this.querySelector('[data-ds-kit-price]');
    if (total) total.textContent = this.#money(soma);

    const was = this.querySelector('[data-ds-kit-was]');
    const save = this.querySelector('[data-ds-kit-save]');
    const economia = somaAntes - soma;
    if (was) {
      was.textContent = this.#money(somaAntes);
      was.hidden = economia <= 0;
    }
    if (save) {
      save.textContent = `Save ${this.#money(economia)}`;
      save.hidden = economia <= 0;
    }
  }
}

if (!customElements.get('ds-bundle')) customElements.define('ds-bundle', DsBundle);
